import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import TagManager from "react-gtm-module";
import SubmitErrorModal from "./SubmitErrorModal";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./ReviewYourPhoneNumber.css";

// Accessing kubernetes deployment constants from config.json
import ConfigContext from '../../context/configContext';
import { useAppSelector, useAppDispatch } from "../../hooks/redux-hooks/hooks";
import { Button, Col, Container, Row } from "react-bootstrap";
import { update } from "../../features/formDataSlice";

const ReviewYourPhoneNumberPage = () => {
  const config = useContext(ConfigContext);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { t } = useTranslation();
  
  const formState = useAppSelector((state) => state.formData.value);
  const dispatch = useAppDispatch();
  
  const customerId = formState.customerId;
  const serviceId = formState.serviceId;


  const navigate = useNavigate();

  const editInfo = () => {
    const formData = JSON.stringify(formState);
    navigate("/");
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "porting_event",
        event_name: "personal_data_finish",
        form_name: "Porting",
        form_step: "review data",
        form_step_action: "Continue"
      },
    });

    // temp for local repro, comment out during merge
    // if (!customerId) {
    //   setCustomerId("525");
    // }
    // if (!serviceId) {
    //   setServiceId("404");
    // }
  }, []);

  // Using React context inside useEffects:
  // https://stackoverflow.com/questions/56240067/accessing-context-from-useeffect
  useEffect(() => {
    if (!formState.eligibilityNumber) {
      window.location.href =
        `${config.ACCT_MGT_PORTING_FRONTEND}/myaccount/accountoverview`;
    }
  }, [config]);

  const submitPhonePorting = async (e: { preventDefault: () => void }) => {

    //prevent resubmission after succesful port submission
    if (formState.portSubmitted) {
      navigate("/transfer-submitted");
      return;
    }
    
    // Set the isLoading state to true when the API request starts
    setIsLoading(true);
    setApiStatus(true);

    TagManager.dataLayer({
      dataLayer: {
        event: "porting_event",
        event_name: "submit_port",
        form_name: "Porting",
        form_step: "review data",
        form_step_action: "Submit Porting"
      },
    });

    const headers = new Headers();
    headers.append("X-REQUEST-ID", formState.uuid);

    e.preventDefault();

    let phoneNumber = formState.eligibilityNumber.replace(/\D/g, "");

    const accountData = {
      firstName: `${formState.firstName}`,
      lastName: `${formState.lastName}`,
      serviceNumberOwnerId: `${formState.serviceNumberOwnerId}`,
      previousAccountNumber: `${formState.accountNumber}`,
      previousAccountPassword: `${formState.accountPIN}`,
      phoneNumber: `${phoneNumber}`,
      address: {
        street1: `${formState.billingAddress}`,
        street2: `${formState.billingAddress2}`,
        city: `${formState.city}`,
        state: `${formState.state}`,
        zipCode: `${formState.zip}`,
      }
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(accountData),
    };

    try {
      if (!customerId || !serviceId) {
        setApiStatus(false);
        window.location.href =
          `${config.ACCT_MGT_PORTING_FRONTEND}/myaccount/accountoverview?portingcallback=true`;
        return;
      }
      const response = (await Promise.race([
        fetch(
          `${config.ACCT_MGT_PORTING_BACKEND}/myaccount/api/porting/port-ins`,
          requestOptions
        ),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("API timed out")), 60000)
        ),
      ])) as Response;

      if (response.status === 200 || response.status === 201) {
        dispatch(update({ ...formState, portSubmitted: true }));

        TagManager.dataLayer({
          dataLayer: {
            event: "porting_event",
            event_name: "successful_port_submission",
            form_name: "Porting",
            form_step: "review data",
            form_step_action: "Submit Porting",
            device_type: formState.landline ? "landline" : "cellphone",
            selected_carrier: formState.provider.toLowerCase() === "zzz_other"
            ? formState.inputProvider
            : formState.provider,
            acct_holder_type: formState.primaryAccountHolder
            ? t("transfer_phone.primaryAccountHolderYes")
            : t("transfer_phone.primaryAccountHolderNo"),
          },
        });
        setApiStatus(true);
        navigate("/transfer-submitted");
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: "porting_event",
            event_name: "failed_port_submission",
            form_name: "Porting",
            form_step: "review data",
            form_step_action: "Submit Porting",
            device_type: formState.landline ? "landline" : "cellphone",
            selected_carrier: formState.provider.toLowerCase() === "zzz_other"
            ? formState.inputProvider
            : formState.provider,
            acct_holder_type: formState.primaryAccountHolder
            ? t("transfer_phone.primaryAccountHolderYes")
            : t("transfer_phone.primaryAccountHolderNo"),
          },
        });

        // handle fetch error responses
        setShowErrorModal(true);
      }
    } catch (error: unknown) {
      // If api errors out, show a generic error modal
      setShowErrorModal(true);
      
      console.error("Error fetching data:", error);
      if (typeof error === "string") {
        // show an error message if the API timed out
        console.error(error);
      }
      setApiStatus(false);
    } finally {
      // Set the isLoading state to false when the API request is completed
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container fluid className="review-phone-container flex-grow-1 h-100">
        <Container fluid className="review-phone-content">
          <Container fluid className="back-container px-0">
            <Row>
              <Col onClick={() => {
                TagManager.dataLayer({
                  dataLayer: {
                    event: "porting_event",
                    event_name: "back_to_acct_overview"
                  },
                });

                window.open(
                  `${config.ACCT_MGT_PORTING_FRONTEND}/myaccount/accountoverview`,
                  "_self"
                );
              }}>
                <FontAwesomeIcon icon={faArrowLeft} />{" "}
                {t("header.back_to_account_overview")}
              </Col>
            </Row>
          </Container>

          <Container fluid className="title-container">
            <Row>
              <Col className="px-0">
                {t("review_phone.title")}
              </Col>
            </Row>
          </Container>

          <Row className="transfer-phone-header-main-divider mx-0">
          </Row>
          <Container fluid className="review-phone-section p-4 pe-0">

            <Row className="mb-3">
              <Col className="fs-2 fw-bold">
                {t("review_phone.subtitle")}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.phoneNumber")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.eligibilityNumber}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.carrier")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.provider.toLowerCase() === "zzz_other"
                  ? formState.inputProvider
                  : formState.provider}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.primaryAccountHolder")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.primaryAccountHolder
                  ? t("transfer_phone.primaryAccountHolderYes")
                  : t("transfer_phone.primaryAccountHolderNo")}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.name")}:</b>{" "}
                <span className="dd-privacy-mask">{`${formState.firstName} ${formState.lastName}`}</span>
              </Col>
            </Row>


            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.billingAddress1")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.billingAddress}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.billingAddress2")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.billingAddress2}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.city")}:</b> <span className="dd-privacy-mask">{formState.city}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.state")}:</b> <span className="dd-privacy-mask">{formState.state}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.zipCode")}:</b> <span className="dd-privacy-mask">{formState.zip}</span>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <b>{t("review_phone.accountNumber")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.accountNumber}</span>
              </Col>
            </Row>

            <Row className="mb-5">
              <Col>
                <b>{t("review_phone.accountTransferPIN")}:</b>{" "}
                <span className="dd-privacy-mask">{formState.accountPIN}</span>
              </Col>
            </Row>


            <Row className="mt-4">
              <Col className="mt-1" xs="auto" sm="auto">
                <Button
                  className="button-secondary review-phone-button-edit-info"
                  disabled={isLoading}
                  onClick={editInfo}>
                  {t("review_phone.editInformation")}
                </Button>
              </Col>

              <Col className="mt-1">
                <Button
                  className="review-phone-form-button button-primary review-phone-button-submit"
                  disabled={isLoading}
                  onClick={submitPhonePorting}>
                  {t("review_phone.submit")}
                </Button>
              </Col>
            </Row>

          </Container>
        </Container>
        {showErrorModal && <SubmitErrorModal setShowModal={setShowErrorModal} />}
      </Container>
    </>
  );
};

export default ReviewYourPhoneNumberPage;
